<script setup>
/* global axios */
// Box de conversão (Meta CAPI) no ticket — padrão dos boxes do fork
// (ConversationScheduledBox). Mostra a origem do anúncio (Click-to-WhatsApp) e o
// atendente escolhe QUAL evento devolver pra Meta (Lead, Contato, Agendamento,
// Cadastro, Compra com valor, etc.) ou marca "Não converteu". Só aparece se a
// conta configurou (gateado no ContactPanel).
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = computed(
  () =>
    `/api/v1/accounts/${accountId}/conversations/${props.conversationId}/meta_capi`
);

// eventos padrão da Meta úteis no funil de WhatsApp. `value` = aceita valor/moeda.
const EVENTS = [
  { name: 'Lead', key: 'LEAD', value: false },
  { name: 'Contact', key: 'CONTACT', value: false },
  { name: 'Schedule', key: 'SCHEDULE', value: false },
  { name: 'CompleteRegistration', key: 'COMPLETE_REGISTRATION', value: false },
  { name: 'SubmitApplication', key: 'SUBMIT_APPLICATION', value: false },
  { name: 'ViewContent', key: 'VIEW_CONTENT', value: true },
  { name: 'AddToCart', key: 'ADD_TO_CART', value: true },
  { name: 'InitiateCheckout', key: 'INITIATE_CHECKOUT', value: true },
  { name: 'StartTrial', key: 'START_TRIAL', value: true },
  { name: 'Subscribe', key: 'SUBSCRIBE', value: true },
  { name: 'Purchase', key: 'PURCHASE', value: true },
];

const loading = ref(true);
const referral = ref({});
const events = ref([]);
const sending = ref(false);
const notConverting = ref(false);
const selected = ref('Lead');
const value = ref('');

const fromAd = computed(
  () => referral.value?.ctwa_clid || referral.value?.source_id
);
const selectedMeta = computed(() =>
  EVENTS.find(e => e.name === selected.value)
);
const supportsValue = computed(() => selectedMeta.value?.value || false);

const statusClass = s =>
  ({
    ok: 'bg-green-500 dark:bg-green-400',
    error: 'bg-red-500 dark:bg-red-400',
    skipped: 'bg-n-slate-8',
  })[s] || 'bg-n-slate-8';

const eventName2Label = name => {
  if (name === 'NotConverted') return t('META_CAPI.BOX.NOT_CONVERTED');
  const meta = EVENTS.find(e => e.name === name);
  return meta ? t(`META_CAPI.EVENTS.${meta.key}`) : name;
};
const eventLabel = e => {
  const base_ = eventName2Label(e.event_name);
  return e.value ? `${base_} · ${e.currency} ${e.value}` : base_;
};

const fmtDate = iso => (iso ? new Date(iso).toLocaleString('pt-BR') : '');

const load = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(base.value);
    const d = data.data || {};
    referral.value = d.referral || {};
    events.value = d.events || [];
  } catch (e) {
    referral.value = {};
    events.value = [];
  } finally {
    loading.value = false;
  }
};

const post = async (eventName, extra = {}) => {
  try {
    const { data } = await axios.post(`${base.value}/send`, {
      event_name: eventName,
      ...extra,
    });
    events.value = data.events || events.value;
    if (data.ok === false) {
      useAlert(t('META_CAPI.BOX.SEND_ERROR', { error: data.error || '' }));
    } else {
      useAlert(t('META_CAPI.BOX.SENT'));
    }
  } catch (e) {
    useAlert(
      e.response?.data?.error || t('META_CAPI.BOX.SEND_ERROR', { error: '' })
    );
  }
};

const sendEvent = async () => {
  sending.value = true;
  const extra = supportsValue.value && value.value ? { value: value.value } : {};
  await post(selected.value, extra);
  value.value = '';
  sending.value = false;
};

const markNotConverted = async () => {
  notConverting.value = true;
  await post('NotConverted');
  notConverting.value = false;
};

watch(() => props.conversationId, load, { immediate: true });
</script>

<template>
  <div class="flex flex-col gap-2 px-3.5 pt-2.5 pb-3.5">
    <div v-if="loading" class="text-xs text-n-slate-10">
      {{ t('META_CAPI.BOX.LOADING') }}
    </div>
    <template v-else>
      <!-- origem do anúncio (Click-to-WhatsApp) -->
      <div
        v-if="fromAd"
        class="flex items-start gap-2 p-1.5 rounded-lg bg-n-alpha-1"
      >
        <span
          class="i-lucide-megaphone size-3.5 mt-0.5 text-n-teal-11 shrink-0"
        />
        <div class="min-w-0">
          <div class="text-sm text-n-slate-12">
            {{ t('META_CAPI.BOX.FROM_AD') }}
          </div>
          <div
            v-if="referral.headline"
            class="text-[0.6875rem] text-n-slate-10 truncate"
          >
            {{ referral.headline }}
          </div>
          <div class="text-[0.6875rem] text-n-slate-10 truncate">
            {{
              referral.source_id
                ? `${t('META_CAPI.BOX.AD_ID')} ${referral.source_id}`
                : referral.ctwa_clid
            }}
          </div>
        </div>
      </div>
      <div v-else class="text-xs text-n-slate-10">
        {{ t('META_CAPI.BOX.NO_AD') }}
      </div>

      <!-- seletor de evento -->
      <label class="text-xs text-n-slate-10">
        {{ t('META_CAPI.BOX.EVENT_FIELD') }}
      </label>
      <select
        v-model="selected"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
      >
        <option v-for="ev in EVENTS" :key="ev.name" :value="ev.name">
          {{ t(`META_CAPI.EVENTS.${ev.key}`) }}
        </option>
      </select>

      <!-- valor (só p/ eventos que aceitam) -->
      <input
        v-if="supportsValue"
        v-model="value"
        type="number"
        min="0"
        step="0.01"
        :placeholder="t('META_CAPI.BOX.VALUE_PLACEHOLDER')"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
      />

      <button
        class="w-full px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="sending"
        @click="sendEvent"
      >
        {{ sending ? t('META_CAPI.BOX.SENDING') : t('META_CAPI.BOX.SEND_EVENT') }}
      </button>

      <button
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-transparent hover:bg-n-alpha-1 hover:text-n-ruby-11 disabled:opacity-50"
        :disabled="notConverting"
        @click="markNotConverted"
      >
        {{ t('META_CAPI.BOX.NOT_CONVERTED_BTN') }}
      </button>

      <!-- histórico -->
      <div
        v-for="e in events.slice(0, 5)"
        :key="e.id"
        class="flex items-center gap-2 py-1.5 border-b border-n-weak last-of-type:border-b-0"
      >
        <span
          class="rounded-full size-1.5 shrink-0"
          :class="statusClass(e.status)"
        />
        <span class="text-sm text-n-slate-12 truncate">
          {{ eventLabel(e) }}
        </span>
        <span class="ml-auto text-[0.6875rem] text-n-slate-10 whitespace-nowrap">
          {{ fmtDate(e.created_at) }}
        </span>
      </div>
    </template>
  </div>
</template>
